import { fDateISO } from 'src/utils/format-time';

import { CONFIG } from 'src/global-config';
import { getData, updateData } from 'src/lib/firebase';

// ----------------------------------------------------------------------

export async function getUserProfile(uid) {
  if (!uid) return {};

  const profileData = await getData({ path: `users/${uid}` });

  if (!profileData?.id) return {};

  return {
    id: profileData.id,
    uid: profileData.uid,
    email: profileData.email,
    city: profileData.city ?? '',
    about: profileData.about ?? '',
    role: profileData.role || 'admin',
    country: profileData.country ?? '',
    coverUrl: profileData.coverUrl ?? '',
    photoURL: profileData.photoURL ?? '',
    totalSales: profileData?.totalSales ?? 0,
    displayName: profileData.displayName ?? '',
    phoneNumber: profileData.phoneNumber ?? '',
    fullAddress: profileData.fullAddress ?? '',
    totalRating: profileData?.totalRating ?? 0,
    totalReviews: profileData.totalReviews ?? 0,
    totalFollowers: profileData?.totalFollowers ?? 0,
    totalFollowing: profileData?.totalFollowing ?? 0,
    companyName: profileData.companyname ?? profileData.displayName,
    verifiedAccount: profileData?.plan === 'pro' || profileData?.plan === 'premium',
    socialLinks: profileData.socialLinks ?? {
      facebook: '',
      instagram: '',
      tiktok: '',
      linkedin: '',
      x: '',
    },
  };
}

export async function getUserData(userServerAuth, firestore) {
  const {
    uid,
    email,
    createdAt,
    isAnonymous,
    lastLoginAt,
    emailVerified,
    photoURL = '',
    displayName = '',
    providerData = [{}],
  } = userServerAuth;

  const [{ phoneNumber = '', providerId = '' }] = providerData;

  const profileData = await getData({ path: `users/${uid}`, firestore });

  let plan = profileData.plan || 'basic';

  const { payer_id } =
    process.env.NODE_ENV === 'development' ? CONFIG.mercadopago.subscriptions.test : profileData;

  const appDomain =
    process.env.NODE_ENV === 'development' ? 'https://localhost:3030' : CONFIG.appDomain;

  const subscriptionData = payer_id
    ? await fetch(
        `${appDomain}/api/mercadopago/subscription?status=authorized&payer_id=${payer_id}`
      ).then((response) => response.json())
    : {};

  if (
    subscriptionData.status === 'authorized' &&
    subscriptionData.summarized?.semaphore === 'green'
  ) {
    const subscriptionPlan = subscriptionData.external_reference
      ?.toLocaleLowerCase()
      .split('plan')
      .pop();
    if (plan !== subscriptionPlan) {
      plan = subscriptionPlan;
      await updateData(`users/${uid}`, { plan }, null, firestore);
    }
  } else {
    if (!subscriptionData.id && fDateISO() > profileData.auto_recurring?.end_date) {
      plan = 'basic';
      await updateData(`users/${uid}`, { plan }, null, firestore);
    }
  }

  if (email !== profileData.email) {
    await updateData(
      `users/${uid}`,
      {
        uid,
        plan,
        email,
        photoURL,
        createdAt,
        providerId,
        lastLoginAt,
        isAnonymous,
        displayName,
        phoneNumber,
        emailVerified,
        notifications: [],
      },
      null,
      firestore
    );
  }

  const countersData = await getData({ path: `counters/${uid}`, firestore });

  const userData = {
    ...userServerAuth,
    ...profileData,
    ...subscriptionData,
    plan,
    id: uid,
    payer_id,
    email: profileData.email,
    city: profileData.city ?? '',
    about: profileData.about ?? '',
    role: profileData.role || 'admin',
    country: profileData.country ?? '',
    coverUrl: profileData.coverUrl ?? '',
    photoURL: profileData.photoURL ?? '',
    accessToken: profileData.accessToken,
    profileCompleted: !!profileData.about,
    totalSales: profileData.totalSales ?? 0,
    totalRating: profileData.totalRating ?? 0,
    displayName: profileData.displayName ?? '',
    phoneNumber: profileData.phoneNumber ?? '',
    fullAddress: profileData.fullAddress ?? '',
    totalReviews: profileData.totalReviews ?? 0,
    totalProducts: countersData.totalProducts ?? 0,
    totalFollowers: profileData.totalFollowers ?? 0,
    totalFollowing: profileData.totalFollowing ?? 0,
    verifiedAccount: plan === 'pro' || plan === 'premium',
    totalCollaborators: countersData.totalCollaborators ?? 0,
    companyName: profileData.companyname || profileData.displayName,
  };

  return JSON.parse(JSON.stringify(userData));
}
